/**
 * Robust Item Translation Service
 * 
 * This service provides reliable translation for inventory item names with multiple fallback strategies:
 * 1. Local dictionary (instant, no API calls)
 * 2. Backend translation API (Gemini AI + Google Translate)
 * 3. Never fails - returns original text if all methods fail
 * 
 * Features:
 * - Caching to prevent redundant translations
 * - Retry logic with exponential backoff
 * - Multiple fallback methods
 * - Error recovery
 */

import http from './httpClient';

class ItemTranslationService {
  constructor() {
    // In-memory cache for translations
    this.translationCache = new Map();
    
    // Track items currently being translated to prevent duplicate requests
    this.translatingItems = new Set();
    
    // Common ingredient dictionary for instant translations (no API call)
    this.commonIngredients = {
      // English to Telugu
      'en-te': {
        'onion': 'ఉల్లిగడ్డ',
        'onions': 'ఉల్లిగడ్డలు',
        'green chilli': 'పచ్చి మిరపకాయ',
        'green chillies': 'పచ్చి మిరపకాయలు',
        'green chilies': 'పచ్చి మిరపకాయలు',
        'tomato': 'టమాటా',
        'tomatoes': 'టమాటాలు',
        'potato': 'బంగాళాదుంప',
        'potatoes': 'బంగాళాదుంపలు',
        'ginger': 'అల్లం',
        'garlic': 'వెల్లుల్లి',
        'coriander': 'కొత్తిమీర',
        'curry leaves': 'కరివేపాకు',
        'turmeric': 'పసుపు',
        'turmeric powder': 'పసుపు పొడి',
        'red chilli': 'ఎర్ర మిరపకాయ',
        'cumin': 'జీలకర్ర',
        'cumin seeds': 'జీలకర్ర',
        'mustard': 'ఆవాలు',
        'rice': 'బియ్యం',
        'basmati rice': 'బాస్మతి బియ్యం',
        'idli rice': 'ఇడ్లీ బియ్యం',
        'wheat': 'గోధుమ',
        'flour': 'పిండి',
        'gram flour besan': 'బేసన్ పిండి',
        'rice flour': 'బియ్యం పిండి',
        'oil': 'నూనె',
        'vegetable oil': 'కూరగాయల నూనె',
        'salt': 'ఉప్పు',
        'sugar': 'చక్కెర',
        'jaggery': 'బెల్లం',
        'milk': 'పాలు',
        'full fat milk': 'పూర్తి కొవ్వు పాలు',
        'butter': 'వెన్న',
        'ghee': 'నెయ్యి',
        'cream': 'క్రీమ్',
        'yogurt': 'పెరుగు',
        'paneer': 'పెన్నీర్',
        'chicken': 'కోడి',
        'chicken whole': 'పూర్తి కోడి',
        'mutton': 'మటన్',
        'mutton or lamb bone in': 'మటన్ లేదా గొర్రె ఎముకతో',
        'fish': 'చేప',
        'egg': 'గుడ్డు',
        'eggs': 'గుడ్లు',
        'baking powder': 'బేకింగ్ పౌడర్',
        'baking soda': 'బేకింగ్ సోడా',
        'basmati rice': 'బాస్మతి బియ్యం',
        'black peppercorns': 'నల్ల మిరియాలు',
        'black pepper': 'నల్ల మిరియాలు',
        'black salt kala namak': 'నల్ల ఉప్పు',
        'black salt': 'నల్ల ఉప్పు',
        'black tea powder': 'నల్ల టీ పొడి',
        'cardamom pods': 'ఏలకులు',
        'cardamom powder': 'ఏలకుల పొడి',
        'cardamom': 'ఏలకులు',
        'carom seeds (ajwain)': 'వాములు',
        'carom seeds': 'వాములు',
        'ajwain': 'వాములు',
        'chat masala': 'చాట్ మసాలా',
        'chickpeas kabuli chana': 'చనా గింజలు',
        'chickpeas': 'చనా గింజలు',
        'kabuli chana': 'చనా గింజలు',
        'chole masala': 'చోలే మసాలా',
        'cinnamon sticks': 'దాల్చిన చెక్క',
        'cinnamon': 'దాల్చిన చెక్క',
        'cloves': 'లవంగాలు',
        'coriander leaves': 'కొత్తిమీర ఆకులు',
        'coriander powder': 'ధనియాల పొడి',
        'coriander': 'కొత్తిమీర',
        'cream or cashew paste': 'క్రీమ్ లేదా జీడిపప్పు పేస్ట్',
        'cream': 'క్రీమ్',
        'cumin seeds': 'జీలకర్ర',
        'cumin powder': 'జీలకర్ర పొడి',
        'cumin': 'జీలకర్ర',
        'curry leaves': 'కరివేపాకు',
        'dried dates chironji': 'ఎండిన ఖర్జూరం చిరోంజీ',
        'dried mango powder (amchur)': 'ఆమ్చూర్ పొడి',
        'dried mango powder amchur': 'ఆమ్చూర్ పొడి',
        'amchur': 'ఆమ్చూర్ పొడి',
        'dry mango powder': 'ఆమ్చూర్ పొడి',
        'fenugreek seeds methi': 'మెంతులు',
        'fenugreek seeds': 'మెంతులు',
        'methi': 'మెంతులు',
        'fresh cilantro': 'కొత్తిమీర ఆకులు',
        'cilantro': 'కొత్తిమీర ఆకులు',
        'garam masala': 'గరం మసాలా',
        'ghee': 'నెయ్యి',
        'ginger': 'అల్లం',
        'ginger garlic paste': 'అల్లం వెల్లుల్లి పేస్ట్',
        'green gram moong dal': 'పెసలు పప్పు',
        'moong dal': 'పెసలు పప్పు',
        'green peas': 'బఠానీలు',
        'peas': 'బఠానీలు',
        'khoya': 'ఖోయా',
        'mandi spice mix': 'మండి మసాలా మిశ్రమం',
        'onions': 'ఉల్లిగడ్డలు',
        'onion': 'ఉల్లిగడ్డ',
        'pani puri masala': 'పాని పూరి మసాలా',
        'poha flattened rice': 'పోహా అరటి బియ్యం',
        'poha': 'పోహా',
        'flattened rice': 'పోహా',
        'potatoes': 'బంగాళాదుంపలు',
        'potato': 'బంగాళాదుంప',
        'rice': 'బియ్యం',
        'roasted cumin powder': 'వేయించిన జీలకర్ర పొడి',
        'rose water': 'గులాబీ నీరు',
        'saffron strands': 'కుంకుమ పువ్వు దారాలు',
        'saffron': 'కుంకుమ పువ్వు',
        'salt': 'ఉప్పు',
        'semolina suji': 'రవ్వ',
        'semolina': 'రవ్వ',
        'suji': 'రవ్వ',
        'rava': 'రవ్వ',
        'tea bags': 'టీ బ్యాగులు',
        'tea': 'టీ',
        'tomatoes': 'టమాటాలు',
        'tomato': 'టమాటా',
        'urad dal black lentils': 'నల్ల మినుము పప్పు',
        'urad dal': 'మినుము పప్పు',
        'black lentils': 'నల్ల మినుము పప్పు',
        'vegetable oil': 'కూరగాయల నూనె',
        'oil': 'నూనె',
        'vermicelli semiya': 'సేమియా',
        'vermicelli': 'సేమియా',
        'semiya': 'సేమియా',
        'water or milk': 'నీరు లేదా పాలు',
        'water': 'నీరు',
        'milk': 'పాలు',
        'yogurt': 'పెరుగు',
        'all purpose flour': 'మైదా పిండి',
        'maida': 'మైదా పిండి',
        'toor dal': 'కంది పప్పు',
        'tamarind': 'చింత పప్పు',
        'tamarind pulp': 'చింత పప్పు',
        'sambar powder': 'సాంబార్ పొడి',
        'cashews': 'జీడిపప్పులు',
        'almonds': 'బాదం పప్పులు',
        'raisins': 'ఎండు ద్రాక్ష',
        'pistachios': 'పిస్తా',
        'mustard seeds': 'ఆవాలు',
        'sesame seeds': 'నువ్వులు',
        'spinach palak': 'పాలకూర',
        'spinach': 'పాలకూర',
        'palak': 'పాలకూర',
        'mixed vegetables': 'మిశ్రమ కూరగాయలు',
        'lemon juice': 'నిమ్మ రసం',
        'lemon': 'నిమ్మ',
        'mint leaves': 'పుదీనా ఆకులు',
        'mint': 'పుదీనా',
        'large bajji mirchi': 'పెద్ద బజ్జి మిరపకాయలు',
        'bajji mirchi': 'బజ్జి మిరపకాయలు'
      },
      // English to Hindi
      'en-hi': {
        'onion': 'प्याज',
        'onions': 'प्याज',
        'green chilli': 'हरी मिर्च',
        'green chillies': 'हरी मिर्च',
        'green chilies': 'हरी मिर्च',
        'tomato': 'टमाटर',
        'tomatoes': 'टमाटर',
        'potato': 'आलू',
        'potatoes': 'आलू',
        'ginger': 'अदरक',
        'garlic': 'लहसुन',
        'coriander': 'धनिया',
        'curry leaves': 'करी पत्ता',
        'turmeric': 'हल्दी',
        'turmeric powder': 'हल्दी पाउडर',
        'red chilli': 'लाल मिर्च',
        'cumin': 'जीरा',
        'cumin seeds': 'जीरा',
        'mustard': 'सरसों',
        'rice': 'चावल',
        'basmati rice': 'बासमती चावल',
        'idli rice': 'इडली चावल',
        'wheat': 'गेहूं',
        'flour': 'आटा',
        'gram flour besan': 'बेसन',
        'rice flour': 'चावल का आटा',
        'oil': 'तेल',
        'vegetable oil': 'वनस्पति तेल',
        'salt': 'नमक',
        'sugar': 'चीनी',
        'jaggery': 'गुड़',
        'milk': 'दूध',
        'full fat milk': 'फुल फैट दूध',
        'butter': 'मक्खन',
        'ghee': 'घी',
        'cream': 'मलाई',
        'yogurt': 'दही',
        'paneer': 'पनीर',
        'chicken': 'मुर्गा',
        'chicken whole': 'पूरा मुर्गा',
        'mutton': 'मटन',
        'mutton or lamb bone in': 'हड्डी के साथ मटन या भेड़ का मांस',
        'fish': 'मछली',
        'egg': 'अंडा',
        'eggs': 'अंडे',
        'baking powder': 'बेकिंग पाउडर',
        'baking soda': 'बेकिंग सोडा',
        'basmati rice': 'बासमती चावल',
        'black peppercorns': 'काली मिर्च',
        'black pepper': 'काली मिर्च',
        'black salt kala namak': 'काला नमक',
        'black salt': 'काला नमक',
        'black tea powder': 'काली चाय पाउडर',
        'cardamom pods': 'इलायची',
        'cardamom powder': 'इलायची पाउडर',
        'cardamom': 'इलायची',
        'carom seeds (ajwain)': 'अजवाइन',
        'carom seeds': 'अजवाइन',
        'ajwain': 'अजवाइन',
        'chat masala': 'चाट मसाला',
        'chickpeas kabuli chana': 'काबुली चना',
        'chickpeas': 'चना',
        'kabuli chana': 'काबुली चना',
        'chole masala': 'छोले मसाला',
        'cinnamon sticks': 'दालचीनी',
        'cinnamon': 'दालचीनी',
        'cloves': 'लौंग',
        'coriander leaves': 'धनिया पत्ते',
        'coriander powder': 'धनिया पाउडर',
        'coriander': 'धनिया',
        'cream or cashew paste': 'मलाई या काजू पेस्ट',
        'cream': 'मलाई',
        'cumin seeds': 'जीरा',
        'cumin powder': 'जीरा पाउडर',
        'cumin': 'जीरा',
        'curry leaves': 'करी पत्ता',
        'dried dates chironji': 'सूखे खजूर चिरोंजी',
        'dried mango powder (amchur)': 'आमचूर पाउडर',
        'dried mango powder amchur': 'आमचूर पाउडर',
        'amchur': 'आमचूर',
        'dry mango powder': 'आमचूर पाउडर',
        'fenugreek seeds methi': 'मेथी',
        'fenugreek seeds': 'मेथी',
        'methi': 'मेथी',
        'fresh cilantro': 'हरा धनिया',
        'cilantro': 'धनिया',
        'garam masala': 'गरम मसाला',
        'ghee': 'घी',
        'ginger': 'अदरक',
        'ginger garlic paste': 'अदरक लहसुन पेस्ट',
        'green gram moong dal': 'मूंग दाल',
        'moong dal': 'मूंग दाल',
        'green peas': 'हरी मटर',
        'peas': 'मटर',
        'khoya': 'खोया',
        'mandi spice mix': 'मंडी मसाला मिश्रण',
        'onions': 'प्याज',
        'onion': 'प्याज',
        'pani puri masala': 'पानी पूरी मसाला',
        'poha flattened rice': 'पोहा चावल',
        'poha': 'पोहा',
        'flattened rice': 'पोहा',
        'potatoes': 'आलू',
        'potato': 'आलू',
        'rice': 'चावल',
        'roasted cumin powder': 'भुना जीरा पाउडर',
        'rose water': 'गुलाब जल',
        'saffron strands': 'केसर के धागे',
        'saffron': 'केसर',
        'salt': 'नमक',
        'semolina suji': 'सूजी',
        'semolina': 'सूजी',
        'suji': 'सूजी',
        'rava': 'सूजी',
        'tea bags': 'टी बैग',
        'tea': 'चाय',
        'tomatoes': 'टमाटर',
        'tomato': 'टमाटर',
        'urad dal black lentils': 'काली उड़द दाल',
        'urad dal': 'उड़द दाल',
        'black lentils': 'काली दाल',
        'vegetable oil': 'वनस्पति तेल',
        'oil': 'तेल',
        'vermicelli semiya': 'सेवईं',
        'vermicelli': 'सेवईं',
        'semiya': 'सेवईं',
        'water or milk': 'पानी या दूध',
        'water': 'पानी',
        'milk': 'दूध',
        'yogurt': 'दही',
        'all purpose flour': 'मैदा',
        'maida': 'मैदा',
        'toor dal': 'तूर दाल',
        'tamarind': 'इमली',
        'tamarind pulp': 'इमली',
        'sambar powder': 'सांभर पाउडर',
        'cashews': 'काजू',
        'almonds': 'बादाम',
        'raisins': 'किशमिश',
        'pistachios': 'पिस्ता',
        'mustard seeds': 'सरसों के बीज',
        'mustard': 'सरसों',
        'sesame seeds': 'तिल',
        'spinach palak': 'पालक',
        'spinach': 'पालक',
        'palak': 'पालक',
        'mixed vegetables': 'मिश्रित सब्जियां',
        'lemon juice': 'नींबू का रस',
        'lemon': 'नींबू',
        'mint leaves': 'पुदीना पत्ते',
        'mint': 'पुदीना',
        'large bajji mirchi': 'बड़ी बज्जी मिर्च',
        'bajji mirchi': 'बज्जी मिर्च'
      }
    };
  }

  /**
   * Get cache key for an item
   */
  getCacheKey(itemName, targetLang) {
    return `${itemName.toLowerCase().trim()}_${targetLang}`;
  }

  /**
   * Check if translation exists in cache
   */
  getFromCache(itemName, targetLang) {
    const key = this.getCacheKey(itemName, targetLang);
    return this.translationCache.get(key);
  }

  /**
   * Store translation in cache
   */
  storeInCache(itemName, targetLang, translation) {
    const key = this.getCacheKey(itemName, targetLang);
    this.translationCache.set(key, translation);
  }

  /**
   * Normalize ingredient name for better matching
   * Removes extra descriptors, punctuation, and standardizes format
   */
  normalizeIngredientName(name) {
    if (!name) return '';
    
    let normalized = name.toLowerCase().trim();
    
    // Remove common descriptors and modifiers
    const descriptorsToRemove = [
      ', chopped', ', finely chopped', ', minced', ', diced', ', sliced',
      ', boiled and cubed', ', boiled', ', cubed', ', frozen',
      ', for frying', ', for cooking', ', fresh', ', dried', ', raw',
      'chopped ', 'finely chopped ', 'minced ', 'diced ', 'sliced ',
      'boiled ', 'cubed ', 'frozen ', 'fresh ', 'dried ', 'raw '
    ];
    
    for (const descriptor of descriptorsToRemove) {
      normalized = normalized.replace(descriptor, '');
    }
    
    // Replace hyphens with spaces for better matching
    normalized = normalized.replace(/-/g, ' ');
    
    // Remove extra spaces
    normalized = normalized.replace(/\s+/g, ' ').trim();
    
    return normalized;
  }

  /**
   * Check common ingredient dictionary with fuzzy matching
   */
  getFromDictionary(itemName, targetLang) {
    const dictKey = `en-${targetLang}`;
    if (!this.commonIngredients[dictKey]) return null;
    
    const dictionary = this.commonIngredients[dictKey];
    
    // Try exact match first
    const normalized = itemName.toLowerCase().trim();
    if (dictionary[normalized]) {
      return dictionary[normalized];
    }
    
    // Try normalized match (removes descriptors)
    const smartNormalized = this.normalizeIngredientName(itemName);
    if (dictionary[smartNormalized]) {
      return dictionary[smartNormalized];
    }
    
    // Try partial match (find dictionary key that contains or is contained in the search term)
    for (const [dictKey, translation] of Object.entries(dictionary)) {
      const dictNormalized = this.normalizeIngredientName(dictKey);
      
      // Check if normalized forms match
      if (smartNormalized === dictNormalized) {
        return translation;
      }
      
      // Check if the ingredient name contains the dictionary key (e.g., "green chilies, chopped" contains "green chilies")
      if (smartNormalized.includes(dictNormalized) || dictNormalized.includes(smartNormalized)) {
        // Ensure it's a meaningful match (at least 4 characters)
        if (dictNormalized.length >= 4 && smartNormalized.length >= 4) {
          return translation;
        }
      }
    }
    
    return null;
  }

  /**
   * Translate using backend API with retry logic
   */
  async translateViaAPI(itemName, targetLang, retryCount = 0) {
    const maxRetries = 2;
    
    try {
      
      const result = await http.post('/translate/item', {
        text: itemName,
        targetLanguage: targetLang
      }, {
        timeout: 10000 // 10 second timeout
      });

      if (result.success && result.translatedText) {
        return {
          success: true,
          translation: result.translatedText,
          method: result.method || 'api',
          confidence: result.confidence || 0.9
        };
      } else {
        throw new Error(result.error || 'Translation API returned no text');
      }
    } catch (error) {
      console.error(`❌ API Translation failed (attempt ${retryCount + 1}):`, error.message);
      
      // Retry with exponential backoff
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.translateViaAPI(itemName, targetLang, retryCount + 1);
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Main translation method with fallback strategy
   * 
   * Strategy:
   * 1. Check cache
   * 2. Check dictionary
   * 3. Call backend API
   * 4. Return original text if all fail (NEVER FAILS)
   */
  async translate(itemName, targetLang) {
    // Validate input
    if (!itemName || typeof itemName !== 'string') {
      console.warn('⚠️ Invalid item name for translation:', itemName);
      return itemName || '';
    }

    if (!targetLang || targetLang === 'en') {
      return itemName; // No translation needed for English
    }

    const normalizedName = itemName.trim();
    const cacheKey = this.getCacheKey(normalizedName, targetLang);

    // Prevent duplicate translation requests
    if (this.translatingItems.has(cacheKey)) {
      // Wait for ongoing translation to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          const cached = this.getFromCache(normalizedName, targetLang);
          if (cached || !this.translatingItems.has(cacheKey)) {
            clearInterval(checkInterval);
            resolve(cached || normalizedName);
          }
        }, 100);
        
        // Timeout after 15 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(normalizedName);
        }, 15000);
      });
    }

    this.translatingItems.add(cacheKey);

    try {
      // 1. Check cache first (instant)
      const cached = this.getFromCache(normalizedName, targetLang);
      if (cached) {
        return cached;
      }

      // 2. Check common dictionary (instant)
      const fromDict = this.getFromDictionary(normalizedName, targetLang);
      if (fromDict) {
        this.storeInCache(normalizedName, targetLang, fromDict);
        return fromDict;
      }

      // 3. Call backend API with retry logic
      const apiResult = await this.translateViaAPI(normalizedName, targetLang);
      
      if (apiResult.success) {
        this.storeInCache(normalizedName, targetLang, apiResult.translation);
        return apiResult.translation;
      }

      // 4. All methods failed - return original text (NEVER FAILS)
      console.warn('⚠️ All translation methods failed for:', normalizedName, '- returning original');
      this.storeInCache(normalizedName, targetLang, normalizedName); // Cache the original to prevent retries
      return normalizedName;

    } catch (error) {
      console.error('❌ Unexpected error in translation service:', error);
      return normalizedName; // Return original text on any error
    } finally {
      this.translatingItems.delete(cacheKey);
    }
  }

  /**
   * Batch translate multiple items
   */
  async translateBatch(items, targetLang) {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    const promises = items.map(item => 
      this.translate(item.name, targetLang).then(translation => ({
        ...item,
        translatedName: translation
      }))
    );

    return Promise.all(promises);
  }

  /**
   * Clear translation cache
   */
  clearCache() {
    this.translationCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.translationCache.size,
      entries: Array.from(this.translationCache.entries())
    };
  }
}

// Export singleton instance
const itemTranslationService = new ItemTranslationService();
export default itemTranslationService;
